import { useApolloClient } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { useDeleteDocumentMutation } from "hooks/documents";
import { useCallback, useState } from "react";

/**
 * Bail out of an in-progress upload. There is no shipping document to detach
 * yet — nothing has been applied — so discard is just deleting the uploaded
 * document, unlike the manifest flow's detach-then-delete.
 */
export function useBillOfLadingDiscard({ onDiscarded }: { onDiscarded: () => void }) {
  const alerts = useAlerts();
  const client = useApolloClient();
  const [deleteDocument] = useDeleteDocumentMutation();

  const [uploadedDocumentId, setUploadedDocumentId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  const discard = useCallback(async () => {
    if (!uploadedDocumentId) {
      setConfirmOpen(false);
      return;
    }
    setDiscarding(true);
    try {
      await deleteDocument({ variables: { id: uploadedDocumentId, retainAsset: false } });
      await client.refetchQueries({ include: "active" });
      alerts.success("Upload discarded.");
      setConfirmOpen(false);
      onDiscarded();
    } catch (err) {
      alerts.error("Failed to discard the upload.", err instanceof Error ? err : undefined);
    } finally {
      setDiscarding(false);
    }
  }, [uploadedDocumentId, deleteDocument, client, alerts, onDiscarded]);

  return {
    setUploadedDocumentId,
    confirmOpen,
    requestConfirm: () => setConfirmOpen(true),
    cancelConfirm: () => setConfirmOpen(false),
    discarding,
    discard,
  };
}
